import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import api from '../api';
import { getCurrentFyStartYear } from '../utils/fy';

const CompanyContext = createContext(null);
const STORAGE_KEY = 'activeCompanyId';

export function CompanyProvider({ children }) {
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyIdState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fyStartYear, setFyStartYear] = useState(() => getCurrentFyStartYear());
  const [toast, setToast] = useState(null);
  const [modalState, setModalState] = useState({ open: false, mode: 'add', company: null });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const setSelectedCompanyId = useCallback((id) => {
    setSelectedCompanyIdState(id);
    if (id) {
      localStorage.setItem(STORAGE_KEY, String(id));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const refreshCompanies = useCallback(() => {
    return api
      .get('/companies')
      .then((res) => {
        setCompanies(res.data);
        setError(null);
        return res.data;
      })
      .catch((err) => {
        setError(err.message);
        return [];
      });
  }, []);

  useEffect(() => {
    refreshCompanies().then((data) => {
      setLoading(false);
      if (data.length > 0) {
        setSelectedCompanyIdState((prev) => {
          if (prev && data.some((c) => c.id === prev)) return prev;

          const stored = Number(localStorage.getItem(STORAGE_KEY));
          if (stored && data.some((c) => c.id === stored)) return stored;

          const fallback = data[data.length - 1];
          localStorage.setItem(STORAGE_KEY, String(fallback.id));
          return fallback.id;
        });
      }
    });
  }, [refreshCompanies]);

  const selectedCompany = companies.find((c) => c.id === selectedCompanyId) || null;

  const selectCompany = useCallback(
    (id) => {
      setSelectedCompanyId(id);
      const company = companies.find((c) => c.id === id);
      if (company) setToast(`Switched to ${company.name}`);
    },
    [companies, setSelectedCompanyId]
  );

  const deleteCompany = useCallback(
    (id) => {
      return api.delete(`/companies/${id}`).then(() => {
        return refreshCompanies().then((data) => {
          if (selectedCompanyId === id) {
            const next = data[data.length - 1];
            setSelectedCompanyId(next ? next.id : null);
          }
        });
      });
    },
    [refreshCompanies, selectedCompanyId, setSelectedCompanyId]
  );

  const openAddModal = useCallback(() => setModalState({ open: true, mode: 'add', company: null }), []);
  const openEditModal = useCallback((company) => setModalState({ open: true, mode: 'edit', company }), []);
  const closeModal = useCallback(() => setModalState({ open: false, mode: 'add', company: null }), []);

  return (
    <CompanyContext.Provider
      value={{
        companies,
        selectedCompanyId,
        setSelectedCompanyId,
        selectCompany,
        deleteCompany,
        selectedCompany,
        loading,
        error,
        refreshCompanies,
        fyStartYear,
        setFyStartYear,
        toast,
        setToast,
        modalState,
        openAddModal,
        openEditModal,
        closeModal,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  return useContext(CompanyContext);
}
